import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toggle } from '@/components/Toggle';
import { apiPutRequest } from '@/utils/apiClient';
import { useUser } from '@/hooks/UserContext';

/**
 * ステップメール（オンボーディング等）の受信可否。メール内の配信停止と同じフラグを更新する。
 */
const MailStepDeliverySettings = (): ReactElement => {
    const { t } = useTranslation('settings');
    const { userInfo, refreshUserInfo } = useUser();

    const [unsubscribed, setUnsubscribed] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');

    useEffect(() => {
        if (!userInfo) return;
        setUnsubscribed(userInfo.mailStepUnsubscribed === true);
    }, [userInfo]);

    const receiveEnabled = !unsubscribed;

    const handleReceiveChange = useCallback(
        async (nextReceive: boolean) => {
            const nextUnsub = !nextReceive;
            setError('');
            setSuccess('');
            setIsSaving(true);
            try {
                const res = await apiPutRequest('/api/user/mail-step-delivery', {
                    mailStepUnsubscribed: nextUnsub,
                });
                if (!res.ok) {
                    throw new Error('save failed');
                }
                setUnsubscribed(nextUnsub);
                await refreshUserInfo();
                setSuccess(t('mailStep.saveSuccess'));
                window.setTimeout(() => setSuccess(''), 4000);
            } catch {
                setError(t('mailStep.saveError'));
            } finally {
                setIsSaving(false);
            }
        },
        [refreshUserInfo, t],
    );

    return (
        <section className="flex flex-col gap-3">
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                    {error}
                </div>
            )}
            {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    {success}
                </div>
            )}
            {isSaving && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-800">
                    {t('mailStep.saving')}
                </div>
            )}

            <div className="min-w-0">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 pb-1">
                    <h3 className="text-lg font-medium">{t('mailStep.heading')}</h3>
                    <Toggle
                        checked={receiveEnabled}
                        onChange={(next: boolean) => void handleReceiveChange(next)}
                        isDisabled={isSaving}
                        className="scale-75 origin-left"
                    />
                </div>
                <p className="text-sm text-gray-600">{t('mailStep.description')}</p>
            </div>
        </section>
    );
};

export default MailStepDeliverySettings;
